import {
  isSectionCompleted,
  isSectionForReview,
  subParaIdsForCompletionToggle,
  type ReaderRouteSection,
} from './sections.js'

export type ReaderProgressFlag = 'completed' | 'review'

export type ReaderProgressLike = {
  subParaId: string
  completed: boolean
  review: boolean
}

export type ReaderProgressState = {
  completedSubParaIds: Set<string>
  reviewSubParaIds: Set<string>
  completedSectionIds: Set<string>
  reviewSectionIds: Set<string>
  currentSubParaIds: string[]
  currentCompleted: boolean
  currentReview: boolean
  completedSectionCount: number
  reviewCount: number
  progressLabel: string
}

export function idsForProgressFlag(
  rows: ReadonlyArray<ReaderProgressLike>,
  flag: ReaderProgressFlag,
): Set<string> {
  const ids = new Set<string>()
  for (const row of rows) {
    if (row[flag]) ids.add(row.subParaId)
  }
  return ids
}

export function currentSectionByIdOrFirst(
  sections: ReadonlyArray<ReaderRouteSection>,
  sectionId: string | null | undefined,
): ReaderRouteSection | null {
  return sections.find((section) => section.id === sectionId) ?? sections[0] ?? null
}

export function adjacentSection(
  sections: ReadonlyArray<ReaderRouteSection>,
  currentIndex: number,
  direction: -1 | 1,
): ReaderRouteSection | null {
  return sections[currentIndex + direction] ?? null
}

export function sectionIdsForProgress(
  sections: ReadonlyArray<ReaderRouteSection>,
  ids: ReadonlySet<string>,
  flag: ReaderProgressFlag,
): Set<string> {
  const sectionIds = new Set<string>()
  const predicate = flag === 'completed' ? isSectionCompleted : isSectionForReview
  for (const section of sections) {
    if (predicate(section, ids)) sectionIds.add(section.id)
  }
  return sectionIds
}

export function buildReaderProgressState(input: {
  sections: ReadonlyArray<ReaderRouteSection>
  currentSection: ReaderRouteSection | null
  completedSubParaIds: ReadonlySet<string>
  reviewSubParaIds: ReadonlySet<string>
}): ReaderProgressState {
  const { sections, currentSection, completedSubParaIds, reviewSubParaIds } = input
  const completedSectionIds = sectionIdsForProgress(sections, completedSubParaIds, 'completed')
  const reviewSectionIds = sectionIdsForProgress(sections, reviewSubParaIds, 'review')
  const currentSubParaIds =
    currentSection != null ? subParaIdsForCompletionToggle(currentSection) : []

  return {
    completedSubParaIds: new Set(completedSubParaIds),
    reviewSubParaIds: new Set(reviewSubParaIds),
    completedSectionIds,
    reviewSectionIds,
    currentSubParaIds,
    currentCompleted:
      currentSection != null ? isSectionCompleted(currentSection, completedSubParaIds) : false,
    currentReview:
      currentSection != null ? isSectionForReview(currentSection, reviewSubParaIds) : false,
    completedSectionCount: completedSectionIds.size,
    reviewCount: reviewSubParaIds.size,
    progressLabel: `${completedSectionIds.size}/${sections.length}`,
  }
}

export function buildReaderProgressStateFromRows(input: {
  sections: ReadonlyArray<ReaderRouteSection>
  currentSection: ReaderRouteSection | null
  progressRows: ReadonlyArray<ReaderProgressLike>
}): ReaderProgressState {
  const completedSubParaIds = idsForProgressFlag(input.progressRows, 'completed')
  const reviewSubParaIds = idsForProgressFlag(input.progressRows, 'review')
  return buildReaderProgressState({
    sections: input.sections,
    currentSection: input.currentSection,
    completedSubParaIds,
    reviewSubParaIds,
  })
}
